import React, { lazy, Suspense } from 'react'
import { Helmet } from 'react-helmet'
const Hero = lazy(() => import('./inner_components/Hero'))
const ServiceCard = lazy(() => import('./ServiceCard'))

// Loading skeleton component
const ServiceCardSkeleton = () => (
  <div className="flex flex-col lg:flex-row items-center gap-8 py-16 px-4 lg:px-8">
    <div className="w-full lg:w-1/2">
      <div className="rounded-lg shadow-md w-full h-[330px] bg-gray-200 animate-pulse" />
    </div>
    <div className="w-full lg:w-1/2 space-y-4">
      <div className="h-8 bg-gray-200 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-4 bg-gray-200 rounded w-full animate-pulse"
          />
        ))}
      </div>
    </div>
  </div>
)

const ServicePage = () => {
  const services = [
    {
      title: 'Employee Transportation Services',
      description:
        'At RoyalRise Tours & Travels, while our brand is newly established, our team brings over 15 years of expertise in delivering safe, cost-effective, and reliable transportation solutions for corporate employees.',
      details: [
        'Technology Integration: Embracing the latest technology to enhance performance',
        'Quality Assurance: Dedicated to delivering high-quality services consistently',
        'Compliance: Strict adherence to all relevant laws and regulations',
        'Clientele: Trusted by major companies for efficient employee transportation',
      ],
      imageSrc: 'images/pexels-cottonbro-4606402.jpg',
    },
    {
      title: 'Corporate Fleet Management',
      description:
        'Our state-of-the-art vehicle management system is managed by an experienced team, offering car rental services tailored for corporate needs. Our luxury fleet is operated by professional drivers who undergo rigorous training.',
      details: [
        'Regulatory Compliance: Strict adherence to laws and regulations',
        '24/7 Control Room: Equipped with advanced infrastructure',
        'Diverse Fleet: Includes buses, tempo travellers, Hybrid vehicles, EVs, SUVs, and sedans',
        'Expertise: Specialized in employee transport solutions',
      ],
      imageSrc: 'images/Corporate Fleet Management.png',
    },
    {
      title: 'Corporate Rentals',
      description:
        'We offer a range of luxury vehicles for corporate clients, professionals, and high-end organizations, all driven by trained chauffeurs. Our services strictly adhere to industry standards to ensure top-notch quality.',
      details: [
        'Luxury Vehicles: Access to high-end cars with professional chauffeurs',
        'Regulatory Adherence: Compliance with all industry rules',
        'Flexible Rental Options: Services for corporates and VIPs',
      ],
      imageSrc: 'images/corporate-rental.jpg',
    },
  ]

  return (
    <>
      <Helmet>
        <title>RoyalRise Tours & Travels</title>
        <meta
          name="description"
          content="RoyalRise Tours & Travels offers reliable employee transportation, fleet management, and luxury vehicle rentals for corporate clients with over 15 years of expertise."
        />
        <meta
          name="keywords"
          content="employee transportation, corporate rentals, fleet management, luxury vehicles, corporate transport, car rentals for business"
        />
        <meta
          property="og:title"
          content="Employee Transportation & Fleet Services | RoyalRise Tours & Travels"
        />
        <meta
          property="og:description"
          content="RoyalRise Tours & Travels offers reliable employee transportation, fleet management, and luxury vehicle rentals for corporate clients with over 15 years of expertise."
        />
        <meta property="og:image" content="/path/to/your/image.jpg" />
        <meta property="og:url" content="https://www.royalrise.com/services" />
      </Helmet>

      {/* Hero Section with Suspense */}
      <Suspense
        fallback={<div className="h-[35rem] bg-gray-200 animate-pulse" />}
      >
        <div data-aos="fade-down" data-aos-duration="500">
          <Hero
            tagline="Our Services"
            title="What We Offer"
            description="Comprehensive corporate transportation solutions tailored to your workforce and business needs."
            bgImage="/images/service.jpeg"
          />
        </div>
      </Suspense>

      {/* Services Section with Suspense */}
      <div className="max-w-7xl mx-auto divide-y divide-gray-200">
        <Suspense
          fallback={
            <>
              {[1, 2, 3].map((i) => (
                <ServiceCardSkeleton key={i} />
              ))}
            </>
          }
        >
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              {...service}
              isReversed={index % 2 !== 0}
              index={index}
            />
          ))}
        </Suspense>
      </div>
    </>
  )
}

export default ServicePage
